'use client';

import {
  useImperativeHandle,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
  type Ref,
} from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './otp-input.module.scss';

export type OtpInputHandle = { focus: (index?: number) => void };

export type OtpInputProps = {
  value: readonly string[];
  onChange: (value: string[]) => void;
  onSubmit?: () => void;
  invalid?: boolean;
  disabled?: boolean;
  label?: string;
  ref?: Ref<OtpInputHandle>;
  className?: string | undefined;
};

/** One-time code cells: digits only, auto-advance, paste, arrows, Backspace. */
export function OtpInput({
  value,
  onChange,
  onSubmit,
  invalid = false,
  disabled = false,
  label = 'Код из SMS',
  ref,
  className,
}: OtpInputProps) {
  const cells = useRef<Array<HTMLInputElement | null>>([]);
  const length = value.length;
  const focusCell = (index: number) => {
    const cell = cells.current[Math.max(0, Math.min(length - 1, index))];
    cell?.focus();
    cell?.select();
  };
  useImperativeHandle(ref, () => ({ focus: (index = 0) => focusCell(index) }));
  const setAt = (index: number, digit: string) => {
    const next = [...value];
    next[index] = digit;
    onChange(next);
  };
  const handleInput = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      setAt(index, '');
      return;
    }
    // A paste or autofill (3+ digits) fills the following cells; typing over a digit keeps the new one (08B).
    if (digits.length > 2) {
      const next = [...value];
      for (let offset = 0; offset < digits.length && index + offset < length; offset += 1)
        next[index + offset] = digits[offset] ?? '';
      onChange(next);
      focusCell(Math.min(length - 1, index + digits.length));
      return;
    }
    setAt(index, digits.slice(-1));
    if (index < length - 1) focusCell(index + 1);
  };
  const handleKey = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      event.preventDefault();
      setAt(index - 1, '');
      focusCell(index - 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusCell(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusCell(index + 1);
    } else if (event.key === 'Enter') {
      onSubmit?.();
    }
  };
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const digits = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!digits) return;
    event.preventDefault();
    onChange(Array.from({ length }, (_, index) => digits[index] ?? ''));
    focusCell(Math.min(length - 1, digits.length));
  };
  // Cells are fixed positions: the key is the position, not the digit.
  const positions = value.map((digit, index) => ({ digit, index, key: `digit-${index + 1}` }));
  return (
    <fieldset
      className={cx(styles.root, className)}
      aria-label={label}
      style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
    >
      {positions.map(({ digit, index, key }) => (
        <input
          key={key}
          ref={(node) => {
            cells.current[index] = node;
          }}
          className={styles.cell}
          data-invalid={invalid || undefined}
          value={digit}
          disabled={disabled}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={length}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Цифра ${index + 1} из ${length}`}
          aria-invalid={invalid || undefined}
          onChange={(event) => handleInput(index, event.target.value)}
          onKeyDown={(event) => handleKey(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </fieldset>
  );
}
